import { useEffect, useState } from 'react';
import { ParsedUrlQuery } from 'querystring';
import { IParamSerializer } from '../types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Shape = Record<string, any>;

type IParamSerializerMap<T extends Shape> = {
	[key in keyof T]: IParamSerializer<T[key]>;
};

type Setter<T extends Shape> = (key: keyof T, value: T[keyof T]) => void;

/**
 * Hook to get default state from URL search parameters
 * This will encode and decode the value to/from the URL
 *
 * @example
 * const { state, set, toParams } = useStateFromParams({
 *   foo: { defaultValue: 0, decode: parseInt, encode: (value) => value.toString() },
 *   bar: { defaultValue: 'baz', decode: (value) => value, encode: (value) => value },
 *   baz: { defaultValue: true, decode: (value) => value === 'true', encode: (value) => value.toString() },
 * });
 * console.log(state); // { foo: 1, bar: 'baz', baz: true }
 * set('foo', 2); // New value will be set
 * console.log(state); // { foo: 2, bar: 'baz', baz: true }
 * // Update the URL
 * history.replaceState(null, '', `?${toParams().toString()}`);
 * // or
 * router.push(`?${toParams().toString()}`);
 * // or
 * window.location.search = toParams().toString();
 *
 * @param definition
 * @param query
 */
export function useStateFromParams<T extends Shape>(
	definition: IParamSerializerMap<T>,
	query?: ParsedUrlQuery,
): {
	state: T;
	set: Setter<T>;
	toParams(): URLSearchParams;
	query?: Readonly<ParsedUrlQuery>;
} {
	const [currentQuery, setCurrentQuery] = useState<ParsedUrlQuery | undefined>(
		query,
	);

	const getState = () => getStateFromParams(definition, query);

	const [state, setState] = useState<T>(getState);

	useEffect(() => {
		setState(getState);
		setCurrentQuery(query);
	}, [query]);

	const set = (key: keyof T, value: T[keyof T]) => {
		setState((previous) => ({ ...previous, [key]: value }));
	};

	const toParams = () => getParamsFromState(definition, state);

	return { state, set, toParams, query: currentQuery };
}

export function getStateFromParams<T extends Shape>(
	definition: IParamSerializerMap<T>,
	query: ParsedUrlQuery = {},
) {
	return Object.entries(definition).reduce(
		(previous, [key, { decode, defaultValue }]) => {
			const value =
				typeof query[key] === 'string'
					? decode(query[key] as string)
					: defaultValue;

			return {
				...previous,
				[key as keyof T]: value as T[keyof T],
			};
		},
		{} as T,
	);
}

export function getParamsFromState<T extends Shape>(
	definition: IParamSerializerMap<T>,
	state: T,
) {
	const params = new URLSearchParams();

	Object.entries(definition).forEach(([key, { encode }]) => {
		const value = encode(state[key]);

		if (value === '') return;

		params.set(key, value);
	});

	return params;
}
